import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { orderBy } from 'lodash-es';
import {getCurrentFolderFromUrl, isProductionMode, supportsShadowDOM} from 'src/js/utils';
import { MAIL_SYSTEM_DIRS as MSD } from 'newJs/config';
import { SORT_OPTIONS } from 'newJs/config';

const exportActions = (store) => {
    const actions = {};
    for (const key in store) {
        if (typeof store[key] === 'function') {
            actions[key] = store[key];
        }
    }
    return actions;
};

const mailListStore = devtools((set/*, get*/) => ({
    currentPage: 1,
    hash: '',
    showAdvancedSearch: false,
    showEmail: false,
    data: {
        messages: []
    },
    checkedMessages: [],
    isFilteredByLabel: false,
    isMarkModal: false,
    search: {
        // One of: advancedSearch, fromAddress, quickSearch, thread, unSeen
        type: '',
        // One of: address, eml keyword, msguid
        keyword: '',
        // Folders list
        inFolders: [getCurrentFolderFromUrl()],
        // inFolders: [MSD.INBOX],
        // Object for Advanced search criteria
        params: {}
    },
    recipientsBackup: {
        isError: false, // will check if triggered atleast one Contacts MAPI response error
        response: { // structure as Contacts MAPI response on successful message send
            contacts: [],
            totalContacts: 0,
            items: 0
        }
    },
    msgSelectors: {
        emlAttachments: 'eml-read__attachments',
        emlBody: supportsShadowDOM ? 'eml-msg-text' : 'messageText',// emlBody: 'messageText',
        emlBodyContainer: 'eml-read__content',
        emlBodyModalContainer: 'eml-read__modal-container',
        emlHeadersContainer: 'eml-read__headers',
        emlOverflowContainer: 'eml-content__overflow',
        emlSubjectContainer: 'eml-read__subject',
        emlToolbarContainer: 'eml-read__toolbar',
        msgTextID: '#messageText',
        appContainer: 'eml-read-app',
        emlHeaderBCC: '#eml-header_bcc',
        emlHeaderCC: '#eml-header_cc',
        emlHeaderTO: '#eml-header_to'
    },
    setMsgSelectors: (obj) => set(
        (state) => ({ msgSelectors: {...state.msgSelectors, ...obj} })
    ),
    toggleShowEmail: () => set(
        (state) => ({ showEmail: !state.showEmail })
    ),
    setMarkModal: () => set(
        (state) => ({ isMarkModal: !state.isMarkModal })
    ),
    setSearch: (obj) => {
        return set((state) => ({
            search: { ...state.search, ...obj }
        }));
    },
    setSearchFolder: (folder = MSD.INBOX) => set(
        (state) => ({
            search: { ...state.search, inFolders: [folder] }
        })
    ),
    setSearchKeywordType: (keyword, type) => set(
        (state) => ({
            search: { ...state.search, keyword, type, params: {} }
        })
    ),
    setSearchKeyword: (needle) => set(
        (state) => ({
            currentPage: 1,
            search: { ...state.search, keyword: needle, type: 'keyword', params: {} }
        })
    ),
    resetSearchKeyword: () => set(
        (state) => ({
            search: { ...state.search, type: '', keyword: '', params: {} }
        })),
    setSearchFrom: (needle) => set(
        (state) => ({
            search: { ...state.search, keyword: needle, type: 'fromAddress', params: {} }
        })
    ),
    setSearchThread: (msguid, folder) => set(
        (state) => ({
            search: { ...state.search, keyword: msguid, type: 'thread', inFolders: [folder] }
        })
    ),
    setSearchUnSeen: (folder) => set(
        () => ({
            search: { inFolders: [folder], keyword: '', type: 'unSeen' }
        })
    ),
    setPageHash: (hash) => set(() => ({ hash })),
    setData: (obj, currentPage, direction, sortFieldId) => set(
        (state) => {
            let messages;
            if (sortFieldId === SORT_OPTIONS.arrival) {
                messages = orderBy(obj.messages, [(message) => new Date(message.recievedDateTime)], [direction]);
            } else if (sortFieldId === SORT_OPTIONS.size) {
                messages = orderBy(obj.messages, [(message) => message.size], [direction]);
            } else {
                messages = obj.messages;
            }
            return ({
                ...state,
                data: {
                    ...state.data,
                    items: obj.items,
                    totalQueryMessages: obj.totalQueryMessages,
                    totalMessages: obj.totalMessages,
                    messages: {
                        ...state.data.messages,
                        [currentPage]: messages
                    }
                }
            });
        }
    ),
    setCurrentPage: (pageNum) => {
        const page = pageNum !== undefined ? +pageNum : 1;
        return set(() => ({ currentPage: page }));
    },
    toggleAdvancedSearch: (force) => set(
        (state) => ({
            showAdvancedSearch: typeof force !== 'undefined'
                ? force
                : !state.showAdvancedSearch
        })
    ),
    toggleEmlContent: (force) => set(
        (state) => ({
            showEmlContent: typeof force !== 'undefined'
                ? force
                : !state.showEmlContent
        })
    ),
    updateCheckedMessages: (payload) => {
        set(() => {
            return { checkedMessages: payload };
        });
    },
    setFilteredByLabel: (bool) => {
        return set(() => {
            return { isFilteredByLabel: bool };
        });
    },
    setContactsMapiError: (val) => set(
        (state) => {
            return ({
                ...state,
                recipientsBackup: {
                    ...state.recipientsBackup,
                    isError: val
                }
            });
        }
    ),
    setContactsRecipientsList: (val) => set(
        (state) => {
            return ({
                recipientsBackup: {
                    ...state.recipientsBackup,
                    response: val
                }
            });
        }
    ),
}), {
    enabled: !isProductionMode(),
    name: 'mail-list-store',
    trace: true,
});

export const useMailListStore = create(mailListStore);

export const useMailListStoreActions = () => useMemo(() => {
    // console.log('actions');
    return exportActions(useMailListStore.getState());
}, []);

let permanentStore = (set/*, get*/) => ({
    // 0 - arrival 1 - from, 2 - subject,, 3 - size
    sortFieldId: 0,
    lastMsgUid: 0,
    sortDirection: 'desc', // default sort direction
    setSortParams: (params = {}) => {
        const { sortDir, sortBy } = Object.fromEntries(params.entries());
        set({
            sortDirection: sortDir || 'desc',
            sortFieldId: sortBy || 0,
        });
    },
    setLastMsgUid: (lastMsgUid) => set({ lastMsgUid: Number(lastMsgUid) }),
});

permanentStore = persist(
    devtools(permanentStore, {
        enabled: !isProductionMode(),
        name: 'mail-list-store_permanent'
    }),
    { name: 'mail-list-store_permanent', }
);

export const useMailListPermanentStore = create(permanentStore);
